'use client';

import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { TableRootContext } from './context/table-root.context';
import { TableRowContext } from './context/table-row.context';
import { tableCellSharedRecipe } from './styles/table-cell-shared.styles';
import { tableCellRecipe } from './styles/table-cell.styles';
import type { TableCellPropsInterface } from './types/table-cell.types';

/**
 * A native [`<td>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/td) element.
 *
 * @since 8.0.0
 */
export const TableCell = forwardRef<HTMLTableCellElement, TableCellPropsInterface>(
  (props: TableCellPropsInterface, forwardedRef) => {
    const { align, children, className, css: cssProp, ...rest } = props;

    const { appearance, size } = useContext(TableRootContext);
    const { interactive, selected } = useContext(TableRowContext);

    const styles = css(
      tableCellSharedRecipe.raw({ align, appearance, size }),
      tableCellRecipe.raw({ interactive, selected }),
      cssProp,
    );

    return (
      <td
        ref={forwardedRef}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Table.Cell"
        className={cx('c11n', styles, className)}
        {...rest}
      >
        {children}
      </td>
    );
  },
);

TableCell.displayName = 'Table.Cell';
